/**
 * Validation-Aware Save Hook
 *
 * Custom hook that wraps the save functionality with validation checks.
 *
 * @since 1.0.0
 */

import { useCallback, useMemo, useState } from 'react';

import { useSettingsContext } from '../../contexts/settings-context';
import { useValidationContext } from '../../contexts/validation-context';
import type { UseValidationAwareSaveReturn, ValidationErrorWithContext } from './types';

/**
 * Custom hook for validation-aware settings save.
 *
 * @since 1.0.0
 *
 * @return Validation-aware save functions and state.
 */
export const useValidationAwareSave = (): UseValidationAwareSaveReturn => {
  const { saveSettings: originalSaveSettings } = useSettingsContext();
  const { hasValidationErrors: hasErrors, getAllValidationErrors } = useValidationContext();
  
  const [showValidationModal, setShowValidationModal] = useState<boolean>(false);

  // Get current validation errors
  const validationErrors: ValidationErrorWithContext[] = useMemo(() => {
    return getAllValidationErrors();
  }, [getAllValidationErrors]);

  const hasValidationErrors = useMemo(() => {
    return hasErrors();
  }, [hasErrors]);

  const saveSettings = useCallback(async (): Promise<void> => {
    // Check for validation errors before saving
    if (hasValidationErrors) {
      // Show validation error modal
      setShowValidationModal(true);
      return;
    }

    // If no validation errors, proceed with normal save
    try {
      await originalSaveSettings();
    } catch {
      // Error handling is managed by the original context
      throw new Error('Save failed');
    }
  }, [hasValidationErrors, originalSaveSettings]);

  const closeValidationModal = useCallback(() => {
    setShowValidationModal(false);
  }, []);

  return {
    saveSettings,
    hasValidationErrors,
    validationErrors,
    showValidationModal,
    closeValidationModal,
  };
};
